import React from "react";
import {
  AlertIndicator,
  alertTypes,
  ModalButtonsContainer,
  TextButton,
} from "@SeemplicityDev/shared";
import DependenciesModal from "../../../../AppView/Dependencies/DependenciesModal";
import type { DependencySet } from "../../../../AppView/Dependencies/types";
import type { AffectedHierarchy, ScopeGuardrailOutcome } from "./types";
import AffectedScopesHierarchy from "./AffectedScopesHierarchy";
import "./ScopeGuardrailModal.scss";

type ScopeGuardrailVariant = "delete" | "edit";

const BLOCKED_TITLE_SUFFIX: Record<ScopeGuardrailVariant, string> = {
  delete: " cannot be deleted.",
  edit: " cannot be edited.",
};

const BLOCKED_MESSAGES: Record<ScopeGuardrailVariant, string> = {
  delete:
    "One or more scopes within this group are in use, and need to be removed from all rules, automations, and widgets connected to it.",
  edit: "It is in use by one or more rules, automations, or widgets.",
};

const ANCESTORS_WARNING_MESSAGES: Record<ScopeGuardrailVariant, string> = {
  delete:
    "Deleting this scope might remove its resources from any rules, automations, and dashboard widgets applying to the scopes hierarchy below.",
  edit: "Editing this scope might change the resources covered by rules, automations, and dashboard widgets applying to the scopes hierarchy below.",
};

export interface ScopeGuardrailModalProps {
  entityName?: string;
  variant?: ScopeGuardrailVariant;
  outcome: ScopeGuardrailOutcome;
  dependencies?: DependencySet;
  hierarchy?: AffectedHierarchy;
  ancestorsHaveDependencies?: boolean;
  // Plain strings only: copy is rendered as text, never as markup.
  blockedMessage?: string;
  message?: string;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onRefresh?: () => void;
}

const ScopeGuardrailModal: React.FC<ScopeGuardrailModalProps> = ({
  entityName,
  variant = "delete",
  outcome,
  dependencies,
  hierarchy,
  ancestorsHaveDependencies = false,
  blockedMessage,
  message,
  confirmLabel,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  onRefresh,
}) => {
  const isBlocked = outcome === "blocked";
  const hasDependencies = !!dependencies && dependencies.total_count > 0;
  const defaultConfirmLabel = variant === "delete" ? "Delete" : "Save";
  const showRefresh = isBlocked && !!onRefresh;

  const actions = (
    <div className={"scope-guardrail-modal-actions"}>
      <TextButton
        buttonStyle={"secondary"}
        label={"Cancel"}
        onClick={onCancel}
        data-testid={"cancel-scope-guardrail"}
      />
      <TextButton
        buttonStyle={variant === "delete" ? "danger" : "primary"}
        label={confirmLabel ?? defaultConfirmLabel}
        onClick={onConfirm}
        disabled={isBlocked || confirmDisabled}
        data-testid={"confirm-scope-guardrail"}
      />
    </div>
  );

  return (
    <div className={"scope-guardrail-modal"}>
      <div className={"scope-guardrail-modal-body"}>
        {isBlocked && !hasDependencies && (
          // The dependencies table carries its own "in use" header, so the
          // banner only explains a block the table doesn't show.
          <div data-testid={"scope-guardrail-blocked-banner"}>
            <AlertIndicator
              type={alertTypes.ERROR}
              content={
                <div className={"scope-guardrail-modal-blocked-content"}>
                  <div>
                    <span className={"scope-guardrail-modal-blocked-name"}>
                      {entityName}
                    </span>
                    {BLOCKED_TITLE_SUFFIX[variant]}
                  </div>
                  <div>{blockedMessage ?? BLOCKED_MESSAGES[variant]}</div>
                </div>
              }
            />
          </div>
        )}
        {ancestorsHaveDependencies && (
          <div data-testid={"ancestors-warning-banner"}>
            <AlertIndicator
              type={alertTypes.WARNING}
              content={ANCESTORS_WARNING_MESSAGES[variant]}
            />
          </div>
        )}
        {!!hierarchy && <AffectedScopesHierarchy hierarchy={hierarchy} />}
        {hasDependencies && (
          <DependenciesModal
            entityName={entityName}
            dependencies={dependencies}
            variant={variant}
            entityLabel={"scope"}
          />
        )}
        {!!message && (
          <div className={"scope-guardrail-modal-message"}>{message}</div>
        )}
      </div>

      <ModalButtonsContainer spaceBetween={showRefresh}>
        {showRefresh && (
          <TextButton
            buttonStyle={"textLink"}
            leftIcon={"seem-icon-refresh"}
            label={"Refresh the Data"}
            onClick={onRefresh}
            data-testid={"refresh-scope-dependencies"}
          />
        )}
        {actions}
      </ModalButtonsContainer>
    </div>
  );
};

export default ScopeGuardrailModal;
